import { useRef, useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Product, ProductInput, TaxCategory } from './products.api';

export interface ProductFormProps {
  formId: string;
  defaultValues?: Product | null;
  onSubmit: (values: ProductInput) => void;
}

const UNITS = [
  { value: 'NIU', label: 'NIU (unidad)' },
  { value: 'KGM', label: 'KGM (kilo)' },
  { value: 'ZZ', label: 'ZZ (servicio)' },
  { value: 'MTR', label: 'MTR (metro)' },
  { value: 'LTR', label: 'LTR (litro)' },
];
const TAX: { value: TaxCategory; label: string }[] = [
  { value: 'GRAVADO', label: 'Gravado' },
  { value: 'EXONERADO', label: 'Exonerado' },
  { value: 'INAFECTO', label: 'Inafecto' },
  { value: 'EXPORTACION', label: 'Exportación' },
  { value: 'GRATUITO', label: 'Gratuito' },
];

function Toggle({ on }: { on: boolean }): JSX.Element {
  return (
    <div style={{ width: 40, height: 23, borderRadius: 99, background: on ? 'var(--success)' : '#CBD5E1', position: 'relative', flex: 'none', transition: 'background .18s' }}>
      <div style={{ position: 'absolute', top: 2, left: on ? 19 : 2, width: 19, height: 19, borderRadius: 99, background: '#fff', transition: 'left .18s', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
    </div>
  );
}

function resizeToDataUrl(file: File, max = 320): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('No se pudo procesar la imagen'));
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => reject(new Error('Imagen inválida'));
    img.src = url;
  });
}

export function ProductForm({ formId, defaultValues, onSubmit }: ProductFormProps): JSX.Element {
  const fileRef = useRef<HTMLInputElement>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(defaultValues?.imageUrl ?? null);
  const [f, setF] = useState({
    sku: defaultValues?.sku ?? '',
    name: defaultValues?.name ?? '',
    description: defaultValues?.description ?? '',
    unitCode: defaultValues?.unitCode ?? 'NIU',
    taxCategory: (defaultValues?.taxCategory ?? 'GRAVADO') as TaxCategory,
    price: defaultValues ? String(defaultValues.price) : '',
    trackInventory: defaultValues?.trackInventory ?? false,
    active: defaultValues?.active ?? true,
  });

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Selecciona una imagen');
    try {
      setImageUrl(await resizeToDataUrl(file));
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      sku: f.sku.trim(),
      name: f.name.trim(),
      description: f.description.trim() || null,
      unitCode: f.unitCode,
      taxCategory: f.taxCategory,
      price: Number(f.price) || 0,
      trackInventory: f.trackInventory,
      active: f.active,
      imageUrl,
    });
  };

  return (
    <form id={formId} onSubmit={submit}>
      <div className="row gap14 aic" style={{ marginBottom: 18 }}>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          style={{
            width: 76,
            height: 76,
            borderRadius: 14,
            border: '1px solid var(--border)',
            background: imageUrl ? 'transparent' : 'var(--canvas)',
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            flex: 'none',
            cursor: 'pointer',
            fontSize: 30,
            color: 'var(--muted-2)',
          }}
          aria-label="Subir imagen"
        >
          {imageUrl ? <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ImagePlus size={24} />}
        </button>
        <div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>
            {imageUrl ? 'Cambiar imagen' : 'Subir imagen'}
          </button>
          <div className="t-xs muted" style={{ marginTop: 6 }}>
            JPG/PNG, se redimensiona automáticamente.
          </div>
          {imageUrl && (
            <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--error)', marginTop: 6, paddingLeft: 0 }} onClick={() => setImageUrl(null)}>
              <X size={13} /> Quitar
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPick} />
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label className="label">Nombre</label>
          <input className="input" value={f.name} required placeholder="Nombre del producto" onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} />
        </div>
        <div className="field">
          <label className="label">SKU</label>
          <input className="input tnum" value={f.sku} required placeholder="FRU-XXX" onChange={(e) => setF((s) => ({ ...s, sku: e.target.value }))} />
        </div>
        <div className="field">
          <label className="label">Precio (incl. IGV)</label>
          <input className="input tnum" type="number" step="0.01" min="0" value={f.price} required placeholder="0.00" onChange={(e) => setF((s) => ({ ...s, price: e.target.value }))} />
        </div>
        <div className="field">
          <label className="label">Unidad</label>
          <select className="select" value={f.unitCode} onChange={(e) => setF((s) => ({ ...s, unitCode: e.target.value }))}>
            {UNITS.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
            {!UNITS.some((u) => u.value === f.unitCode) && <option value={f.unitCode}>{f.unitCode}</option>}
          </select>
        </div>
        <div className="field">
          <label className="label">Afectación IGV</label>
          <select className="select" value={f.taxCategory} onChange={(e) => setF((s) => ({ ...s, taxCategory: e.target.value as TaxCategory }))}>
            {TAX.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label className="label">Descripción</label>
          <textarea className="input" rows={2} value={f.description} placeholder="Opcional" onChange={(e) => setF((s) => ({ ...s, description: e.target.value }))} />
        </div>
      </div>

      <label className="row aic gap10 card card-pad" style={{ marginTop: 16, padding: 14, cursor: 'pointer' }} onClick={() => setF((s) => ({ ...s, trackInventory: !s.trackInventory }))}>
        <Toggle on={f.trackInventory} />
        <div>
          <div className="fw6 t-sm">Controla inventario</div>
          <div className="t-xs muted">Descuenta stock en cada venta</div>
        </div>
      </label>
      <label className="row aic gap10 card card-pad" style={{ marginTop: 10, padding: 14, cursor: 'pointer' }} onClick={() => setF((s) => ({ ...s, active: !s.active }))}>
        <Toggle on={f.active} />
        <div>
          <div className="fw6 t-sm">Activo</div>
          <div className="t-xs muted">Disponible para venta en el POS</div>
        </div>
      </label>
    </form>
  );
}
